import { motion } from 'framer-motion';
import { Zap, TrendingUp } from 'lucide-react';

interface XPProgressProps {
    level: number;
    levelTitle: string;
    currentXP: number;
    nextLevelXP: number;
    compact?: boolean;
}

export function XPProgress({
    level,
    levelTitle,
    currentXP,
    nextLevelXP,
    compact = false
}: XPProgressProps) {
    const progress = nextLevelXP > 0
        ? Math.min(100, (currentXP / nextLevelXP) * 100)
        : 100;
    const xpNeeded = nextLevelXP - currentXP;

    if (compact) {
        return (
            <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 text-sm">
                    <Zap className="w-4 h-4 text-amber-500" />
                    <span className="font-medium">Nv. {level}</span>
                </div>
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                        className="h-full bg-gradient-to-r from-amber-500 to-yellow-500"
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 rounded-xl p-4 border border-amber-500/20">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center">
                        <span className="text-white font-bold">{level}</span>
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Nível</p>
                        <p className="font-medium text-amber-500">{levelTitle}</p>
                    </div>
                </div>

                <div className="text-right">
                    <p className="text-sm text-muted-foreground">Total XP</p>
                    <p className="font-bold text-lg">{currentXP.toLocaleString()}</p>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="relative">
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                        className="h-full bg-gradient-to-r from-amber-500 to-yellow-500 relative"
                    >
                        {/* Shine effect */}
                        <motion.div
                            animate={{ x: ['-100%', '200%'] }}
                            transition={{ repeat: Infinity, duration: 2, ease: 'linear' }}
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                        />
                    </motion.div>
                </div>

                {/* Progress Text */}
                <div className="flex items-center justify-between mt-2 text-sm">
                    <span className="text-muted-foreground">
                        {Math.round(progress)}% para o próximo nível
                    </span>
                    {xpNeeded > 0 && (
                        <span className="flex items-center gap-1 text-amber-500">
                            <TrendingUp className="w-3.5 h-3.5" />
                            <span>{xpNeeded.toLocaleString()} XP restantes</span>
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}
